import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FunctionField, usePermissions, useDataProvider, SimpleShowLayout, TextField, ReferenceField, useTranslate } from 'react-admin';
import { Box, Typography, Button, useTheme } from '@mui/material';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import getLoadingState from '@/helpers/ui/getLoadingState.jsx';
import DetailsMap from '@/components/global/map/DetailsMap.jsx';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import CustomMorePopover from '@/components/global/buttons/CustomMorePopover';
import CustomEditModalButton from '@/components/global/buttons/CustomEditModalButton.jsx';
import { useDrawer } from '@/composables/useDrawer';
import DeviceDetailsStats from './DeviceDetailsStats.jsx';
import UnassignedField from '@/components/global/fields/UnassignedField';
import DeviceDetailsCollapse from './details/DeviceDetailsCollapse.jsx';
import DeviceDetailsContactInfo from './details/DeviceDetailsContactInfo.jsx';
import { ContractorLogoField } from '@/components/contractors/ContractorLogoField.jsx';
import CreateServiceRequestButton from '@/components/device/CreateServiceRequestButton.jsx';
import { ShowDeviceConfigButton } from '@/components/device/buttons/ShowDeviceConfigButton.jsx';
import {
    ActivateDeviceActionButton,
    DeactivateDeviceActionButton,
    RestartDeviceActionButton,
    UnregisterDeviceActionButton,
} from '@/components/global/buttons/actionButtons.jsx';
import { canViewDeviceConfig } from '@/helpers/device/deviceConfig.js';
import { Palette } from '@/themes/themeOverrides/Palette';
import { getDeviceModelOptionText } from '@/helpers/parameter/parameterSubsetDisplay.js';

const hasAssignedCollectionPoint = record => !!(record?.collectionPointId ?? record?.collectionPoint?.id);

const MoreActionsPopover = ({ permissions, deviceId, record, onStatusChange, color = 'secondary', isOnNewDevicePage }) => {
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const openCreateDrawer = () => {
        openDrawer(record.id, 'service-request-create');
    };
    const canCreateServiceRequest = !isOnNewDevicePage && hasAssignedCollectionPoint(record);
    const canManageDevice = permissions.isAdmin || permissions.isServiceMaster;
    const showConfig = canViewDeviceConfig(permissions);

    if (!record || (!canManageDevice && !showConfig)) return null;

    return (
        <CustomMorePopover permissions={permissions} record={deviceId} buttonLabel={translate('common.options')} className='more-area-in-details' color={color}>
            {canManageDevice && (
                <>
                    {permissions.isAdmin && (
                        <CustomEditModalButton
                            onOpen={() => openDrawer(deviceId, isOnNewDevicePage ? 'new-device-edit' : 'device-edit')}
                            label={translate('resources.device.labels.editDevice')}
                        />
                    )}
                    {canCreateServiceRequest && <CreateServiceRequestButton onClick={openCreateDrawer} />}
                    <RestartDeviceActionButton id={record.id} label={translate('resources.device.labels.restartDevice')} />
                    {record.registrationDate && record.collectionPoint?.operator === 'Kaucja' && (
                        <UnregisterDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.unregisterDevice')}
                            onActionSuccess={onStatusChange}
                            operator={record.collectionPoint.operator}
                            registrationDate={record.registrationDate}
                        />
                    )}
                    {record.isActive ? (
                        <DeactivateDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.deactivateDevice')}
                            onActionSuccess={onStatusChange}
                        />
                    ) : (
                        <ActivateDeviceActionButton
                            id={record.id}
                            label={translate('resources.device.labels.activateDevice')}
                            onActionSuccess={onStatusChange}
                        />
                    )}
                </>
            )}
            {showConfig && <ShowDeviceConfigButton id={record.id} />}
        </CustomMorePopover>
    );
};

export const DeviceDetailsShow = ({ handleClose, elementId }) => {
    const dataProvider = useDataProvider();
    const [device, setDevice] = useState(null);
    const [collectionPoint, setCollectionPoint] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { permissions } = usePermissions();
    const { openDrawer } = useDrawer();
    const theme = useTheme();
    const location = useLocation();
    const [groupName, setGroupName] = useState('');
    const [transactionStats, setTransactionStats] = useState(null);
    const [isTransactionStatsLoading, setIsTransactionStatsLoading] = useState(false);
    const [transactionStatsError, setTransactionStatsError] = useState(null);
    const [periodFilters, setPeriodFilters] = useState({ period: 30 });
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [isErrorsOpen, setIsErrorsOpen] = useState(false);
    const [isServiceHistoryOpen, setIsServiceHistoryOpen] = useState(false);
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const isAdminOrBok = useMemo(() => permissions.isAdmin || permissions.isBok, [permissions]);
    const canEditContractor = permissions.isAdmin || permissions.isBok || permissions.isClientMaster;
    const translate = useTranslate();
    const loadingState = getLoadingState({ isLoading, error, data: device, translate });

    const isOnNewDevicePage = location.pathname.includes('/new-device');

    const fetchTransactionStats = useCallback(() => {
        setIsTransactionStatsLoading(true);
        dataProvider
            .getList('analytic-transactions', {
                filter: { deviceId: elementId, ...periodFilters },
                pagination: { page: 1, perPage: 1 },
                sort: { field: 'id', order: 'DESC' },
            })
            .then(({ data }) => {
                setTransactionStats(data || null);
                setTransactionStatsError(null);
            })
            .catch(err => {
                setTransactionStats(null);
                setTransactionStatsError(err);
            })
            .finally(() => setIsTransactionStatsLoading(false));
    }, [dataProvider, elementId, periodFilters]);

    useEffect(() => {
        fetchTransactionStats();
    }, [fetchTransactionStats]);

    useEffect(() => {
        if (!elementId) return;

        const fetchDeviceData = async () => {
            try {
                setIsLoading(true);
                const { data: deviceData } = await dataProvider.getOne('device', { id: elementId });
                setDevice(deviceData);

                if (deviceData.collectionPointId) {
                    const { data: collectionPointData } = await dataProvider.getOne('collection-point', { id: deviceData.collectionPointId });
                    setCollectionPoint(collectionPointData);
                }

                if (deviceData.groupId) {
                    if (permissions?.isService) {
                        return;
                    }
                    const { data: groupData } = await dataProvider.getOne('device-group', { id: deviceData.groupId });
                    setGroupName(groupData.name);
                }

                setError(null);
            } catch (err) {
                console.error('Error fetching device data:', err);
                setError(err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeviceData();
    }, [dataProvider, elementId, refreshKey, permissions?.isService]);

    if (loadingState) return loadingState;

    if (isLoading || !permissions) return null;

    return (
        <Box>
            <Box
                className='popup-wrapper popup-wrapper-top'
                sx={{
                    padding: '32px 56px 32px !important',
                    borderBottom: `2px solid ${Palette.gray.gray100}`,
                    [theme.breakpoints.down('md')]: {
                        padding: '24px 20px !important',
                    },
                }}
            >
                <Box className='popup-box popup-box--header'>
                    <Typography variant='h4'>{translate('resources.device.labels.deviceInfo')}</Typography>
                    <Box className='popup-box__more-actions-grey'>
                        <MoreActionsPopover
                            permissions={permissions}
                            deviceId={elementId}
                            record={device}
                            onStatusChange={() => setRefreshKey(k => k + 1)}
                            color='secondary'
                            isOnNewDevicePage={isOnNewDevicePage}
                        />
                        <CustomCloseButton onClose={handleClose} />
                    </Box>
                </Box>
            </Box>
            <Box className='border-bottom'>
                <Box className='popup-box'>
                    <SimpleShowLayout record={device}>
                        <Box
                            className='details-summary'
                            sx={{
                                display: 'flex',
                                flexWrap: 'nowrap',
                                gap: '20px',
                                padding: '0 56px',
                                marginTop: '40px',
                                marginBottom: !isOnNewDevicePage ? '0' : '40px',
                                [theme.breakpoints.down('md')]: {
                                    flexWrap: 'wrap',
                                    padding: '0 20px',
                                },
                            }}
                        >
                            <Box
                                className='details-sample-list'
                                sx={{
                                    maxWidth: '374px',
                                    [theme.breakpoints.down('md')]: {
                                        maxWidth: '100%',
                                        minWidth: '100%',
                                    },
                                    '& .MuiStack-root.ra-field': {
                                        padding: '10px 0 !important',
                                        '& .RaLabeled-label': {
                                            width: '110px !important',
                                            minWidth: '110px !important',
                                        },
                                        '& span': {
                                            minWidth: '244px',
                                        },
                                    },
                                    '& .MuiTypography-root.MuiTypography-body2': {
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                {device?.group?.contractor && (
                                    <Box
                                        sx={{ marginBottom: '32px', cursor: canEditContractor ? 'pointer' : 'default', maxWidth: 'max-content' }}
                                        onClick={canEditContractor ? () => openDrawer(device.contractorId, 'contractor-edit') : undefined}
                                    >
                                        <ContractorLogoField contractorSource='group.contractor' source='logo' width={80} marginBottom='32px' />
                                    </Box>
                                )}
                                <SimpleShowLayout>
                                    <TextField source='contractorName' label={translate('common.contractor')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <TextField source='serialNumber' label={translate('resources.device.fields.serialNumber')} />
                                </SimpleShowLayout>
                                {isAdminOrBok ? (
                                    <SimpleShowLayout>
                                        {device?.groupId ? (
                                            <FunctionField
                                                source='groupId'
                                                label={translate('resources.device.fields.group')}
                                                render={() => (
                                                    <Button
                                                        onClick={() => openDrawer(device.groupId, 'device-group-show')}
                                                        className='list-group'
                                                        label={groupName}
                                                        disableRipple
                                                    >
                                                        {groupName}
                                                    </Button>
                                                )}
                                            />
                                        ) : (
                                            <FunctionField label={translate('resources.device.fields.group')} render={() => <UnassignedField />} />
                                        )}
                                    </SimpleShowLayout>
                                ) : (
                                    <SimpleShowLayout>
                                        <FunctionField
                                            source='group.name'
                                            label={translate('resources.device.fields.group')}
                                            render={record => record?.group?.name}
                                        />
                                    </SimpleShowLayout>
                                )}
                                <SimpleShowLayout>
                                    {device?.collectionPoint?.customId ? (
                                        <FunctionField
                                            source='collectionPointId'
                                            label={translate('common.collectionPoint')}
                                            render={record => (
                                                <Button
                                                    onClick={() => openDrawer(record.collectionPointId, 'collection-point-show')}
                                                    className='list-group'
                                                    disableRipple
                                                    sx={{
                                                        justifyContent: 'flex-start',
                                                    }}
                                                >
                                                    {record.collectionPoint.customId}
                                                </Button>
                                            )}
                                        />
                                    ) : (
                                        <FunctionField label={translate('common.collectionPoint')} render={() => <UnassignedField />} />
                                    )}
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <FunctionField
                                        source='collectionPoint'
                                        label={translate('resources.device.labels.location')}
                                        render={() =>
                                            collectionPoint ? `${collectionPoint.address}, ${collectionPoint.postalCode} ${collectionPoint.city}` : ''
                                        }
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <FunctionField
                                        source='collectionPoint'
                                        label={translate('common.contact')}
                                        render={() => (
                                            <DeviceDetailsContactInfo
                                                contactName={collectionPoint?.contactName}
                                                contactEmail={collectionPoint?.contactEmail}
                                                contactPhoneNumber={collectionPoint?.contactPhoneNumber}
                                            />
                                        )}
                                    />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <TextField source='description' label={translate('common.description')} />
                                </SimpleShowLayout>
                                <SimpleShowLayout>
                                    <FunctionField
                                        label={translate('resources.device.fields.collectionType')}
                                        render={record => {
                                            let value = record.collectionType;
                                            if (value) {
                                                return translate(`resources.device.choices.collectionType.${record.collectionType}`);
                                            }

                                            return <UnassignedField />;
                                        }}
                                    />
                                </SimpleShowLayout>
                                {isAdminOrBok && (
                                    <SimpleShowLayout>
                                        <ReferenceField source='modelId' reference='device-model' label={translate('common.model')} link={false} emptyText='—'>
                                            <FunctionField render={model => getDeviceModelOptionText(model)} />
                                        </ReferenceField>
                                    </SimpleShowLayout>
                                )}
                            </Box>
                            <DetailsMap record={device} collectionPoint={collectionPoint} />
                        </Box>
                        {!isOnNewDevicePage && (
                            <Box sx={{ padding: '0 56px', [theme.breakpoints.down('md')]: { padding: '0 20px' } }}>
                                <DeviceDetailsStats
                                    analyticData={transactionStats}
                                    isTransactionStatsLoading={isTransactionStatsLoading}
                                    transactionStatsError={transactionStatsError}
                                    periodFilters={periodFilters}
                                    setPeriodFilters={setPeriodFilters}
                                    elementId={elementId}
                                />
                            </Box>
                        )}
                        <DeviceDetailsCollapse
                            isSummaryOpen={isSummaryOpen}
                            setIsSummaryOpen={setIsSummaryOpen}
                            isErrorsOpen={isErrorsOpen}
                            setIsErrorsOpen={setIsErrorsOpen}
                            isServiceHistoryOpen={isServiceHistoryOpen}
                            setIsServiceHistoryOpen={setIsServiceHistoryOpen}
                            isLogsOpen={isLogsOpen}
                            setIsLogsOpen={setIsLogsOpen}
                            theme={theme}
                            record={device}
                            UnassignedField={UnassignedField}
                            elementId={elementId}
                        />
                    </SimpleShowLayout>
                </Box>
            </Box>
        </Box>
    );
};

DeviceDetailsShow.propTypes = {
    handleClose: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
};

MoreActionsPopover.propTypes = {
    permissions: PropTypes.shape({
        isAdmin: PropTypes.bool,
        isServiceMaster: PropTypes.bool,
        isService: PropTypes.bool,
        isBok: PropTypes.bool,
    }).isRequired,
    deviceId: PropTypes.string.isRequired,
    record: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        isActive: PropTypes.bool,
    }),
    onStatusChange: PropTypes.func,
    color: PropTypes.string,
    isOnNewDevicePage: PropTypes.bool,
};
